import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig } from '@umijs/max';
import {
  history,
  Link,
  type RunTimeLayoutConfig,
  request as requestApi,
  useAccess,
} from '@umijs/max';
import { App, message } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  MyTodo,
  Question,
  SelectLang,
} from './components';
import { ForceInitialPasswordModal } from './components/ForceInitialPasswordModal';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import DownloadCenter from './components/DownloadCenter';
import { FRONTEND_VERSION } from './constants';
import { errorConfig, setGlobalMessageApi } from './requestErrorConfig';
import { setGlobalAntdApp } from './utils/antdApp';
import { initMessageApi } from './utils/request';

export { getInitialState } from './getInitialState';

// import { DEFAULT_AVATAR } from './constants/avatars';
const isDev = process.env.NODE_ENV === 'development';
const loginPaths = ['/user/login', '/user/login-classic'];

const DEFAULT_AVATAR = require('./assets/user/robot-avatar.png');

// 版本显示组件
const VersionDisplay: React.FC = () => {
  const [backendVersion, setBackendVersion] = useState<string>('0.2.0');

  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const response = await requestApi<{ result?: string }>(
          '/settings/version',
          {
            method: 'GET',
            skipErrorHandler: true, // 跳过错误处理，避免版本号获取失败时显示错误提示
          },
        );
        if (response && response.result) {
          setBackendVersion(response.result);
        }
      } catch (error) {
        // 静默失败，使用默认版本号
        console.warn('获取后端版本号失败，使用默认版本号:', error);
      }
    };

    fetchBackendVersion();
  }, []);

  return (
    <>
      <span>前端: {FRONTEND_VERSION}</span>
      <span style={{ marginLeft: 10 }}>后端: {backendVersion}</span>
    </>
  );
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      <MyTodo key="myTodo" />,
      <DownloadCenter key="downloadCenter" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar || DEFAULT_AVATAR,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: (() => {
        const name = initialState?.currentUser?.name || '';
        // const phone = initialState?.currentUser?.phone || '';
        const employeeNumber = initialState?.currentUser?.employeeNumber || '';

        // 获取手机号后四位
        // const phoneLastFour = phone.length >= 4 ? phone.slice(-4) : phone || '';
        // 组合成：用户名-手机号后四位
        return employeeNumber ? `${name}-${employeeNumber}` : name;
      })(),
      fontSize: 20, // 字体大小设置为 20px（比默认的16px更大）
      // fontColor: 'rgba(0, 0, 0, 0.15)', // 确保字体颜色可见
    },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        !loginPaths.includes(location.pathname)
      ) {
        history.replace(loginPaths[0]);
        return;
      }

      // 如果访问根路由，根据用户权限动态跳转
      if (location.pathname === '/') {
        const userMenuCodes = initialState?.userMenuCodes || [];

        // 定义菜单优先级顺序（按路由配置顺序）
        const menuPriority = [
          'operations', // 运营概况
          'customer', // 客户管理
          'task', // 任务管理
          'report', // 报告管理
          'activity', // 活动管理
          'finance', // 财务管理
          'platform', // 平台维护
          'system', // 系统管理
        ];

        // 检查用户是否有除个人设置外的其他菜单权限
        const hasOtherMenuPermission = menuPriority.some((menuCode) =>
          userMenuCodes.includes(menuCode),
        );

        if (hasOtherMenuPermission) {
          // 有其他菜单权限，按优先级跳转
          for (const menuCode of menuPriority) {
            if (userMenuCodes.includes(menuCode)) {
              history.replace(`/${menuCode}`);
              return;
            }
          }
        } else {
          // 只有个人设置权限，跳转到个人设置
          history.replace('/personal');
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // links: isDev
    //   ? [
    //     // <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //     <Link key="openapi" to="#" target="_blank">
    //       <LinkOutlined />
    //       <span>version: 0.2.0</span>
    //     </Link>,
    //   ]
    //   : [],
    links: [
      <Link key="openapi" to="#" target="_blank">
        {/* <LinkOutlined /> */}
        <VersionDisplay />
      </Link>,
    ],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;

      // 创建消息API实例并初始化全局请求拦截器
      const appApi = App.useApp();
      const [messageApi, contextHolder] = message.useMessage();
      React.useEffect(() => {
        initMessageApi(messageApi);
        // 设置全局消息API实例，供 requestErrorConfig 使用
        setGlobalMessageApi(messageApi);
        setGlobalAntdApp(appApi);
      }, [appApi, messageApi]);

      return (
        <>
          {contextHolder}
          <GlobalErrorBoundary>
            <ForceInitialPasswordModal />
            {children}
          </GlobalErrorBoundary>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 注释掉硬编码的baseURL，使用代理配置
  // baseURL: 'https://proapi.azurewebsites.net',

  // 使用 requestErrorConfig.ts 中的统一配置
  // 包括：API 路径处理、认证头添加、401 状态码处理
  ...errorConfig,
};
